import { useState, useEffect, useMemo, useCallback } from "react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { usePatchSaveDoc } from "@/lib/hooks/usePatchSaveDoc";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { useQueryClient } from "@tanstack/react-query";

interface DiscountsEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: DocFieldSchema[];
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  disabled?: boolean;
  isSaving?: boolean;
  documentName?: string;
}

/**
 * Specialized modal for editing discount fields with:
 * 1. Bi-directional calculation between amount and percentage
 * 2. Live preview of discount calculation
 */
export function DiscountsEditModal({
  open,
  onOpenChange,
  fields,
  formData,
  onFieldChange,
  onSaveSuccess,
  disabled,
  isSaving,
  documentName,
}: DiscountsEditModalProps) {
  const [localFormData, setLocalFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { doctype: contextDoctype } = useDynamicView();
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      editFields: getUI("modal_edit_fields", "Edit Discount"),
      updateInformationBelow: getUI("modal_update_information_below", "Adjust discount amount or percentage"),
      save: getUI("modal_save", "Save"),
      saving: getUI("modal_saving", "Saving..."),
      saveChanges: getUI("modal_save_changes", "Save changes"),
      discountPreview: "Discount Preview",
      subtotal: "Subtotal",
      discountAmount: "Discount",
      afterDiscount: "After Discount",
    };
  }, [language]);

  const canSaveDirectly = !!contextDoctype && !!documentName;

  const detailKey = useMemo(() => {
    if (contextDoctype && documentName) {
      return [contextDoctype.toLowerCase(), documentName] as const;
    }
    return null;
  }, [contextDoctype, documentName]);

  const directSaveMutation = usePatchSaveDoc({
    doctype: contextDoctype || "",
    docname: documentName || "",
    detailKey: detailKey || [],
  });

  // Get base amount for calculation (net_total or grand_total based on apply_discount_on)
  // This version takes current state as parameter to avoid stale closure issues
  const getBaseAmountFromState = useCallback((currentState: Record<string, any>) => {
    const applyOn = currentState.apply_discount_on || formData.apply_discount_on || 'Grand Total';
    if (applyOn === 'Net Total') {
      return parseFloat(currentState.net_total ?? formData.net_total ?? 0);
    }
    // For Grand Total, we need to use net_total + taxes before discount
    return parseFloat(currentState.net_total ?? formData.net_total ?? 0) +
           parseFloat(currentState.total_taxes_and_charges ?? formData.total_taxes_and_charges ?? 0);
  }, [formData]);

  // For display purposes - uses localFormData
  const getBaseAmount = useCallback(() => {
    return getBaseAmountFromState(localFormData);
  }, [getBaseAmountFromState, localFormData]);

  // Initialize local form data when modal opens
  useEffect(() => {
    if (open) {
      setLocalFormData({ ...formData });
      setSubmitError(null);
    }
  }, [open, formData]);

  // Handle field changes with bi-directional calculation
  const handleFieldChange = useCallback((fieldname: string, value: any) => {
    setLocalFormData((prev) => {
      const updated = { ...prev, [fieldname]: value };

      // Get base amount using current state (not stale closure)
      const baseAmount = getBaseAmountFromState(updated);

      // Helper functions that use the current base amount
      const calcAmount = (percentage: number) => (percentage / 100) * baseAmount;
      const calcPercentage = (amount: number) => baseAmount <= 0 ? 0 : (amount / baseAmount) * 100;

      // Bi-directional calculation for discount fields
      // ERPNext uses additional_discount_percentage and additional_discount_amount as the main pair
      if (fieldname === 'additional_discount_percentage') {
        const percentage = parseFloat(value) || 0;
        updated.additional_discount_amount = calcAmount(percentage);
      } else if (fieldname === 'additional_discount_amount') {
        const amount = parseFloat(value) || 0;
        updated.additional_discount_percentage = calcPercentage(amount);
      }
      // Also handle discount_percentage and discount_amount pair
      else if (fieldname === 'discount_percentage') {
        const percentage = parseFloat(value) || 0;
        updated.discount_amount = calcAmount(percentage);
      } else if (fieldname === 'discount_amount') {
        const amount = parseFloat(value) || 0;
        updated.discount_percentage = calcPercentage(amount);
      }

      return updated;
    });
    setSubmitError(null);
  }, [getBaseAmountFromState]);

  const computePatch = (): Record<string, any> => {
    const patch: Record<string, any> = {};
    for (const fieldname of Object.keys(localFormData)) {
      if (localFormData[fieldname] !== formData[fieldname]) {
        patch[fieldname] = localFormData[fieldname];
      }
    }
    return patch;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || disabled) return;

    const patch = computePatch();
    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (canSaveDirectly && detailKey) {
        const updatedDoc = await directSaveMutation.mutateAsync(patch);
        Object.keys(patch).forEach((fieldname) => {
          onFieldChange(fieldname, patch[fieldname]);
        });
        if (updatedDoc && detailKey) {
          queryClient.setQueryData(detailKey, updatedDoc);
          queryClient.invalidateQueries({ queryKey: detailKey });
        }
        if (onSaveSuccess && updatedDoc) {
          onSaveSuccess(updatedDoc);
        }
        onOpenChange(false);
        return;
      }

      // Draft mode for new documents - apply discount fields only
      // NOTE: Do NOT calculate grand_total/rounded_total client-side!
      // The parent component should use useDocCalculation hook to get server-calculated totals
      if (!documentName) {
        Object.keys(patch).forEach((fieldname) => {
          onFieldChange(fieldname, patch[fieldname]);
        });
        onOpenChange(false);
        return;
      }

      setSubmitError("Save failed: No save handler available.");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save changes';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Lock/unlock scroll
  useEffect(() => {
    if (open) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [open]);

  // We use hardcoded fields, so no need for visibility filtering
  // Just check for read-only status from passed fields
  const fieldReadOnly: Record<string, boolean> = useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const field of fields) {
      result[field.fieldname] = field.read_only === 1;
    }
    return result;
  }, [fields]);
  const { mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();

  // Get currency symbol
  const currency = localFormData.currency || formData.currency || 'THB';
  const currencySymbols: Record<string, string> = {
    'THB': '฿',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
  };
  const currencySymbol = currencySymbols[currency] || currency;

  // Calculate preview values
  // In ERPNext, discount_amount is CALCULATED from additional_discount_percentage
  // They are the SAME discount, so we use additional_discount_amount for preview
  // (since that's what the user is editing in this modal)
  const baseAmount = getBaseAmount();
  const totalDiscountAmount = parseFloat(localFormData.additional_discount_amount) || 0;
  const afterDiscount = baseAmount - totalDiscountAmount;
  const applyOn = localFormData.apply_discount_on || formData.apply_discount_on || 'Grand Total';

  // Format currency for display
  const formatCurrency = (amount: number) => {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Render field input - matching MetaFieldEditModal style
  const renderFieldInput = (field: DocFieldSchema, value: any, onChange: (value: any) => void) => {
    const computedReadOnly = fieldReadOnly[field.fieldname] ?? field.read_only === 1;
    const isReadOnly = disabled || computedReadOnly;
    const isPercentage = field.fieldname?.includes('percentage') || field.fieldtype === 'Percent';
    const isCurrencyField = field.fieldtype === 'Currency';

    if (field.fieldtype === 'Select') {
      const options = field.options?.split("\n").filter(Boolean) || [];
      return (
        <Select value={value || ""} onValueChange={onChange} disabled={isReadOnly}>
          <SelectTrigger className="w-auto min-w-[180px] text-right justify-end !bg-transparent border-0 p-0 h-auto text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:ring-0 focus:outline-none [&>svg]:ml-2 [&>svg]:text-gray-400">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent align="end">
            {options.map((option) => (
              <SelectItem key={option} value={option} className="text-[15px]">
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Currency or Percent fields - matching MetaFieldEditModal inline style
    // Format the display value - round to 2 decimal places for display
    const displayValue = value !== null && value !== undefined && value !== ""
      ? (typeof value === 'number' ? parseFloat(value.toFixed(2)) : value)
      : "";

    return (
      <div className="flex items-center gap-1">
        {isCurrencyField && (
          <span className="text-[17px] text-gray-500 dark:text-gray-400">{currencySymbol}</span>
        )}
        <Input
          type="number"
          value={displayValue}
          onChange={(e) => {
            const numValue = e.target.value === "" ? null : Number(e.target.value);
            onChange(numValue);
          }}
          disabled={isReadOnly}
          step="0.01"
          placeholder="0.00"
          className="w-[140px] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 placeholder:text-gray-400"
        />
        {isPercentage && (
          <span className="text-[17px] text-gray-500 dark:text-gray-400">%</span>
        )}
      </div>
    );
  };

  // Define the discount fields we need for bi-directional calculation
  // These are hardcoded because we need both percentage and amount fields for calculation to work
  const discountFieldDefinitions: DocFieldSchema[] = useMemo(() => [
    {
      fieldname: 'apply_discount_on',
      fieldtype: 'Select',
      label: 'Apply Discount On',
      options: 'Grand Total\nNet Total',
    } as DocFieldSchema,
    {
      fieldname: 'additional_discount_percentage',
      fieldtype: 'Float',
      label: 'Additional Discount %',
    } as DocFieldSchema,
    {
      fieldname: 'additional_discount_amount',
      fieldtype: 'Currency',
      label: 'Additional Discount Amount',
    } as DocFieldSchema,
  ], []);

  // Use our hardcoded field definitions, but allow passed fields to override labels
  const orderedFields = useMemo(() => {
    // Create a map of passed fields for label overrides
    const passedFieldMap = new Map(fields.map(f => [f.fieldname, f]));

    // Merge our definitions with any passed field data (for labels, etc.)
    return discountFieldDefinitions.map(def => {
      const passed = passedFieldMap.get(def.fieldname);
      if (passed) {
        return { ...def, label: passed.label || def.label };
      }
      return def;
    });
  }, [fields, discountFieldDefinitions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden max-w-[680px] w-[90vw]"
      )}>
        {/* Top fade-out gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none" />

        {/* Checkmark button */}
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
          disabled={isSaving || disabled || isSubmitting || directSaveMutation.isPending}
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
          title={uiLabels.saveChanges}
        >
          <Check className="w-5 h-5 text-white" />
          <span className="sr-only">{uiLabels.save}</span>
        </Button>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative">
            <DialogHeader className="px-10 pt-20 pb-4 text-center">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
                {uiLabels.editFields}
              </DialogTitle>
              <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
                {uiLabels.updateInformationBelow}
              </DialogDescription>
              {submitError && (
                <div className="mt-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
                  <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                </div>
              )}
            </DialogHeader>

            <div className="pb-8 px-10 pt-2 space-y-6">
              {/* Discount Fields */}
              <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                <div className="space-y-0">
                  {orderedFields.map((field, index) => {
                    const isLast = index === orderedFields.length - 1;
                    const resolvedLabel = resolveLabel(contextDoctype || "", field, mode, style, enableSystemTranslations);

                    return (
                      <div
                        key={field.fieldname}
                        className={cn(
                          "flex items-center justify-between min-h-[52px] px-5",
                          !isLast && "border-b border-gray-200/40 dark:border-gray-700/40"
                        )}
                      >
                        <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                          {resolvedLabel.label}
                        </label>
                        <div className="flex justify-end">
                          {renderFieldInput(
                            field,
                            localFormData[field.fieldname],
                            (value) => handleFieldChange(field.fieldname, value)
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Discount Preview Section */}
              <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                <div className="px-5 py-4 border-b border-gray-200/40 dark:border-gray-700/40">
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                    {uiLabels.discountPreview}
                  </h3>
                  <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
                    Applied on {applyOn}
                  </p>
                </div>
                <div className="px-5 py-4 space-y-4">
                  {/* Subtotal */}
                  <div className="flex items-center justify-between">
                    <span className="text-[15px] text-gray-600 dark:text-gray-400">
                      {applyOn === 'Net Total' ? 'Net Total' : 'Grand Total (before discount)'}
                    </span>
                    <span className="text-[15px] font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(baseAmount)}
                    </span>
                  </div>

                  {/* Discount breakdown - show the discount being edited */}
                  {(localFormData.additional_discount_percentage > 0 || localFormData.additional_discount_amount > 0) && (
                    <div className="flex items-center justify-between">
                      <span className="text-[15px] text-gray-600 dark:text-gray-400">
                        Discount ({parseFloat(localFormData.additional_discount_percentage || 0).toFixed(2)}%)
                      </span>
                      <span className="text-[15px] font-medium text-red-600 dark:text-red-400">
                        -{formatCurrency(parseFloat(localFormData.additional_discount_amount || 0))}
                      </span>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="border-t border-gray-200 dark:border-gray-700" />

                  {/* After Discount */}
                  <div className="flex items-center justify-between">
                    <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                      {uiLabels.afterDiscount}
                    </span>
                    <span className="text-[17px] font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(Math.max(0, afterDiscount))}
                    </span>
                  </div>

                  {/* Savings indicator */}
                  {totalDiscountAmount > 0 && baseAmount > 0 && (
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[13px] text-gray-500 dark:text-gray-400">
                        Total savings
                      </span>
                      <span className="text-[13px] font-medium text-red-600 dark:text-red-400">
                        -{formatCurrency(totalDiscountAmount)} ({((totalDiscountAmount / baseAmount) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom fade-out gradient */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none" />
          </div>

          {/* Sticky Save Button */}
          <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="submit"
              disabled={isSaving || disabled || isSubmitting}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving || isSubmitting ? uiLabels.saving : uiLabels.save}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
